import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetAccumulatedFundings } from "../../../../hooks/api/account/useGetAccumulatedFundings";
import { useOrder } from "../../../../hooks/api/order/useOrder";
import { useToast } from "../../../../hooks/toast";
import { ToastEnum, ToastStatusEnum } from "../../../../utils/toast";
import { PnLCardModal } from "../../../PnLCardModal";
import { ClosePositionOverlayInfo } from "../../ClosePositionOverlayInfo";
import BaseDetailsModal from "../BaseDetailsModal";
import { computePosInfo } from "./helpers";
import PositionDetailsContent from "./PositionDetailsContent";
import { IPositionDetailsModalProps } from "./types";

function PositionDetailsModal({
  position,
  onHide,
  style,
}: IPositionDetailsModalProps) {
  const { closePosition } = useOrder();
  const { addToast } = useToast();
  const { t } = useTranslation("app", {
    keyPrefix: "shared.ViewDetailsModal.PositionDetailsModal",
  });
  const { t: apiErrors } = useTranslation("apiErrors");
  const { data: accumulatedFundingData } = useGetAccumulatedFundings();

  const [showCopied, setShowCopied] = useState(false);
  const [showClosePositionConfirmation, setShowClosePositionConfirmation] =
    useState(false);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [showPnLCard, setShowPnLCard] = useState<boolean>(false);

  useEffect(() => {
    setError(undefined);
  }, [showClosePositionConfirmation]);

  // Dismiss copied text after 1 second
  useEffect(() => {
    if (showCopied) {
      const timeout = setTimeout(() => {
        setShowCopied(!showCopied);
      }, 500);
      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [showCopied]);

  const onOpenPositionConfirmationModal = useCallback(() => {
    setShowClosePositionConfirmation(true);
  }, []);

  const onClosePositionConfirmationModal = useCallback(() => {
    setShowClosePositionConfirmation(false);
  }, []);

  const onHideModal = useCallback(() => {
    onHide?.();
    setShowClosePositionConfirmation(false);
  }, [onHide]);

  const onOpenPnLCard = useCallback(() => {
    setShowPnLCard(true);
  }, []);

  const onHidePnLCard = useCallback(() => {
    setShowPnLCard(false);
  }, []);

  const onClosePosition = useCallback(async () => {
    if (!position) {
      return;
    }

    try {
      setError(undefined);
      setLoading(true);
      await closePosition(position);
      onHide?.();
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Position Closed",
          subheader: "Your position was successfully closed",
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    } catch (createError: any) {
      setError(apiErrors(createError.message) || t("error_close_position"));
    } finally {
      setLoading(false);
    }
  }, [addToast, apiErrors, closePosition, onHide, position, t]);

  const posInfo = useMemo(
    () => computePosInfo(position, accumulatedFundingData),
    [position, accumulatedFundingData]
  );

  if (!showPnLCard) {
    return (
      <BaseDetailsModal
        instrument={
          position
            ? {
                instrument_name: position.instrument_name,
                expiry: position.option?.expiry,
                strike: position.option?.strike,
                markPrice:
                  posInfo?.mark !== undefined ? String(posInfo.mark) : undefined,
              }
            : undefined
        }
        show={!!position}
        onHide={onHideModal}
        style={style}
      >
        {position && (
          <ClosePositionOverlayInfo
            show={showClosePositionConfirmation}
            onClose={onClosePositionConfirmationModal}
            loading={loading}
            error={error}
            onClosePosition={onClosePosition}
            position={position}
          />
        )}

        {position && (
          <PositionDetailsContent
            position={position}
            posInfo={posInfo}
            onOpenPositionConfirmationModal={onOpenPositionConfirmationModal}
            onOpenPnLCard={onOpenPnLCard}
          />
        )}
      </BaseDetailsModal>
    );
  }

  return (
    <PnLCardModal
      pnlType="position"
      pnlData={position}
      onHideModal={onHideModal}
      show={!!position}
      onHidePnLCard={onHidePnLCard}
    />
  );
}

export default PositionDetailsModal;
